import { Divider, Group, Modal, Stack } from '@mantine/core';
import { useClipboard, useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useRetrievePrompt, useShare } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import type { IconNames } from '@repo/foundations';
import { Button, Icon, IconButton, Select, Text } from '@repo/foundations';
import { forwardRef, useEffect, useState } from 'react';
import { useAuthUser } from '../../api';
import type { ItemProps } from '../Integration/IntegrationMapping';

export interface IAIShareButtonProps {
	promptId: string;
}

const getSelectDescription = (value: string, workspaceName: string) => {
	switch (value) {
		case 'Only you':
			return 'Only you can view this conversation';
		default:
			if (workspaceName && value === workspaceName) {
				return 'Anyone in this workspace with the link can view';
			}
			return '';
	}
};

const getIcon = (value: string, workspaceName: string) => {
	switch (value) {
		case 'Only you':
			return <Icon name="lock" />;
		default:
			if (workspaceName && value === workspaceName) {
				return <Icon name="building" />;
			}
			return null;
	}
};

const SelectItemComponent = forwardRef<
	HTMLDivElement,
	ItemProps & { workspaceName: string }
>(
	(
		{ label, workspaceName, ...others }: ItemProps & { workspaceName: string },
		ref
	) => (
		<div ref={ref} {...others}>
			<Group noWrap spacing="sm">
				{getIcon(label, workspaceName)}
				<div>
					<Text size="sm">{label}</Text>
					<Text size="xs" color="text/secondary/default">
						{getSelectDescription(label, workspaceName)}
					</Text>
				</div>
			</Group>
		</div>
	)
);

SelectItemComponent.displayName = 'SelectItemComponent';

export default function AIShareButton({ promptId }: IAIShareButtonProps) {
	const [opened, { open, close }] = useDisclosure(false);
	const [shared, setShared] = useState(false);
	const { copy } = useClipboard();
	const { user, workspace } = useAuthUser();

	const { data: prompt } = useRetrievePrompt(
		{
			pathParams: {
				promptId,
			},
		},
		{
			enabled: !!promptId && promptId !== 'new',
		}
	);

	const { mutateAsync: sharePrompt } = useShare({});

	useEffect(() => {
		if (prompt && prompt.shared_globally) {
			setShared(true);
		}
	}, [prompt?.shared_globally]);

	// Do not render a share button if prompt ID is not provided and cannot be inferred
	if (promptId === '' || user?.id !== prompt?.user?.id) {
		return null;
	}

	const workspaceName = workspace?.name || 'Secoda';

	const handleSelectChange = (value: string) => {
		setShared(value === workspaceName);
		sharePrompt({
			pathParams: {
				promptId: promptId || '',
			},
			body: {
				shared_globally: value === workspaceName,
			},
		});
	};

	const handleCopyLink = () => {
		const url = `${window.location.origin}/ai/${promptId}`;
		copy(url);
		showNotification({
			title: 'Link copied',
			message: 'Link copied to clipboard',
			color: 'green',
		});
	};

	const handleShareButtonClick = () => {
		trackEvent('ai/share/click', { prompt_id: promptId }, user, workspace);
		open();
	};

	const selectValue = shared ? workspaceName : 'Only you';

	return (
		<>
			<Modal opened={opened} onClose={close} title="Share chat">
				<Stack spacing="md">
					<Stack spacing="xs">
						<Text size="sm" color="text/primary/default" weight="bold">
							Who can access
						</Text>
						<Select
							data={[
								{
									label: 'Only you',
									value: 'Only you',
									iconName: 'lock' as IconNames,
									workspaceName: workspaceName,
								},
								{
									label: workspaceName,
									value: workspaceName,
									iconName: 'building' as IconNames,
									workspaceName: workspaceName,
								},
							]}
							value={selectValue}
							onChange={handleSelectChange}
							renderIcon={(item) => <Icon name={item.iconName} />}
							itemComponent={SelectItemComponent}
							withinPortal
						/>
						<Text size="xs" color="text/secondary/default">
							{getSelectDescription(selectValue, workspaceName)}
						</Text>
					</Stack>
					<Divider />
					<Group position="apart">
						<Button onClick={handleCopyLink}>Copy Link</Button>
						<Button variant="primary" onClick={close}>
							Done
						</Button>
					</Group>
				</Stack>
			</Modal>
			<IconButton
				onClick={handleShareButtonClick}
				iconName="share"
				tooltip="Share chat"
				variant="tertiary"
			/>
		</>
	);
}
